import loadable from '@loadable/component'
import withHydrationOnDemand from 'react-hydration-on-demand'

/* Constants */
import {DAVINCI_EVENT_NAMES} from '../analytics/davinci/davinciConstants'

/* Components */
const ProductDetailCarousel = loadable(() => import('../../views/product/desktop/carousel/ProductDetailCarousel'))
const Recommendation = loadable(() => import('../../views/product/desktop/recommendation/Recommendation'))
const SponsoredProducts = loadable(() => import('../../views/product/desktop/sponsored-products/SponsoredProducts'))
const Summary = loadable(() => import('../../views/product/desktop/summary/Summary'))
const BuyboxSeller = loadable(() => import('../../views/product/desktop/buybox-seller/BuyboxSeller'))
const Variant = loadable(() => import('../../views/product/desktop/variant/Variant'))
const Price = loadable(() => import('../../views/product/desktop/price/Price'))
const AddToCart = loadable(() => import('../../views/product/desktop/add-to-cart/AddToCart'))
const MerchantCoupons = loadable(() => import('../../views/product/desktop/merchant-coupon/MerchantCoupons'))
const CampaignBundle = loadable(() => import('../../views/product/desktop/campaign-bundle/CampaignBundle'))
const MinQuantity = loadable(() => import('../../views/product/desktop/min-quantity/MinQuantity'))
const KeyFeatures = loadable(() => import('../../views/product/desktop/key-features/KeyFeatures'))
const Campaigns = loadable(() => import('../../views/product/desktop/campaigns/Campaigns'))
const BuybackRow = loadable(() => import('../../views/product/desktop/buyback-row/BuybackRow'))
const SizeReview = loadable(() => import('../../views/product/desktop/size-review/SizeReview'))
const OtherMerchants = loadable(() => import('../../views/product/desktop/other-merchants/OtherMerchants'))
const PremiumContent = loadable(() => import('../../views/product/desktop/premium-content/PremiumContent'))
const Tab = loadable(() => import('../../views/product/desktop/tab/Tab'))
const DeliveryOptions = loadable(() => import('../../views/product/desktop/delivery-options/DeliveryOptions'))
const ProductBadges = loadable(() => import('../../views/product/desktop/product-badges/ProductBadges'))
const PaymentOptionsRow = loadable(() => import('../../views/product/desktop/payment-options-row/PaymentOptions'))
const PopularKeyword = loadable(() => import('../../views/product/desktop/popular-keyword/PopularKeyword'))
const Vas = loadable(() => import('../../views/product/desktop/vas/Vas'))
const GreenBand = loadable(() => import('../../views/product/desktop/green-band/GreenBand'))
const ComparedProducts = loadable(() => import('../../components/ComparedProducts/ComparedProducts'))

/* Utils */
import {
  generateProductDetailCarouselProps,
  generateSponsoredProductsProps,
  generateRecommendationProps,
  generateSummaryProps,
  generateVariantProps,
  generateAddToCartProps,
  generateBuyboxSellerProps,
  generateMerchantCouponsProps,
  generateCampaignBundleProps,
  generateKeyFeaturesProps,
  generateCampaignsProps,
  generateBuybackRowProps,
  generateSizeReviewProps,
  generateOtherMerchantsProps,
  generatePremiumBannerProps,
  generateTabProps,
  generateMinQuantityProps,
  generatePriceProps,
  generateDeliveryOptionsProps,
  generateProductBadgesProps,
  generatePaymentOptionsProps,
  generatePopulerKeywordProps,
  generateVasProps,
  generateGreenBandProps,
  generateComparedProductsProps,
} from './productDesktopUtils'

const INTERSECTION_ROOT_MARGIN = '100px 0px 100px 0px'

const PRODUCT_DETAIL_VIEW_TYPES = {
  IMAGE_BASED: '3:4',
  SUMMARY_BASED: '1:1'
}

const PRODUCT_DETAIL_VIEW_TYPES_NAME = {
  '3:4': 'IMAGE_BASED',
  '1:1': 'SUMMARY_BASED'
}

const PRODUCT_DETAIL_CAROUSEL_IMAGE_SIZES = {
  [PRODUCT_DETAIL_VIEW_TYPES.IMAGE_BASED]: {
    width: 424,
    height: 600
  },
  [PRODUCT_DETAIL_VIEW_TYPES.SUMMARY_BASED]: {
    width: 424,
    height: 424
  }
}

const PRODUCT_DETAIL_EXTENDED_CAROUSEL_IMAGE_SIZES = {
  [PRODUCT_DETAIL_VIEW_TYPES.IMAGE_BASED]: {
    width: 480,
    height: 640
  },
  [PRODUCT_DETAIL_VIEW_TYPES.SUMMARY_BASED]: {
    width: 640,
    height: 640
  }
}

const PRODUCT_DETAIL_EXTENDED_CAROUSEL_THUMBNAIL_SIZES = {
  [PRODUCT_DETAIL_VIEW_TYPES.IMAGE_BASED]: {
    width: 48,
    height: 64
  },
  [PRODUCT_DETAIL_VIEW_TYPES.SUMMARY_BASED]: {
    width: 64,
    height: 64
  }
}

const PRODUCT_DETAIL_CLASSNAME = {
  0: 'firstColumn',
  1: 'secondColumn',
  2: 'thirdColumn'
}

const PRODUCT_DETAIL_COMPONENTS = {
  Image: {
    component: ProductDetailCarousel,
    propsMapper: generateProductDetailCarouselProps,
  },
  Summary: {
    component: Summary,
    propsMapper: generateSummaryProps,
  },
  BuyboxSeller: {
    component: BuyboxSeller,
    propsMapper: generateBuyboxSellerProps,
  },
  Variant: {
    component: Variant,
    propsMapper: generateVariantProps,
  },
  Price: {
    component: Price,
    propsMapper: generatePriceProps,
  },
  AddToCart: {
    component: AddToCart,
    propsMapper: generateAddToCartProps,
  },
  Recommendation: {
    component: withHydrationOnDemand({on: [['scroll', () => document]]})(Recommendation),
    propsMapper: generateRecommendationProps,
  },
  SponsoredProducts: {
    component: SponsoredProducts,
    propsMapper: generateSponsoredProductsProps,
  },
  MerchantCoupon: {
    component: MerchantCoupons,
    propsMapper: generateMerchantCouponsProps,
  },
  CampaignBundle: {
    component: CampaignBundle,
    propsMapper: generateCampaignBundleProps,
  },
  ProductKeyFeatures: {
    component: KeyFeatures,
    propsMapper: generateKeyFeaturesProps,
  },
  Campaigns: {
    component: Campaigns,
    propsMapper: generateCampaignsProps,
  },
  Buyback: {
    component: BuybackRow,
    propsMapper: generateBuybackRowProps,
  },
  SizeReview: {
    component: SizeReview,
    propsMapper: generateSizeReviewProps,
  },
  OtherMerchants: {
    component: OtherMerchants,
    propsMapper: generateOtherMerchantsProps,
  },
  Premium: {
    component: PremiumContent,
    propsMapper: generatePremiumBannerProps,
  },
  Tab: {
    component: Tab,
    propsMapper: generateTabProps,
  },
  MinQuantity: {
    component: MinQuantity,
    propsMapper: generateMinQuantityProps,
  },
  DeliveryOptions: {
    component: DeliveryOptions,
    propsMapper: generateDeliveryOptionsProps,
  },
  ProductBadges: {
    component: ProductBadges,
    propsMapper: generateProductBadgesProps,
  },
  PaymentOptionsRow: {
    component: PaymentOptionsRow,
    propsMapper: generatePaymentOptionsProps,
  },
  PopularKeyword: {
    component: PopularKeyword,
    propsMapper: generatePopulerKeywordProps,
  },
  Vas: {
    component: Vas,
    propsMapper: generateVasProps,
  },
  GreenBand: {
    component: GreenBand,
    propsMapper: generateGreenBandProps,
  },
  FeaturedSnippet: {
    component: withHydrationOnDemand({on: ['visible']})(ComparedProducts),
    propsMapper: generateComparedProductsProps,
  },
}

const EVENT_BUS_NAMES = {
  VARIANT_CHANGE: 'variantChangedForProductDetail',
  DUE_DATE: 'voltran.dueDate',
  VAS_ADD: 'CAT.VASAdd',
  VAS_REMOVE: 'CAT.VASRemove',
  VAS_RESET: 'CAT.VASReset',
  lOCATION_CHANGE: 'shipping.voltran.shippingLocation.location.change',
  FASTER_SELLER_CLICK: 'storefront.voltran.deliveryOptions.fasterSeller.click',
  FETCH_PDP_CAMPAIGN: 'FetchPDPCampaign',
  LOYALTY_INFO: 'voltran.loyaltyInfo',
}

const ADD_TO_CART_BUTTON_TEXT = {
  DEFAULT: 'Sepete ekle',
  IS_IN_STOCK: 'Sepete ekle',
  IS_PRE_ORDER: 'Ön sipariş ver'
}

const PDP_LAYOUT = {
  LEFT: 0,
  CENTER: 1,
  RIGHT: 2
}

const PRODUCT_INFORMATION_TEXT = {
  IS_CAMPAIGN_PRODUCT: {
    description: 'Kampanya ürünüdür, satışı bulunmamaktadır.',
  },
  IS_PRE_ORDER: {
    description: '{date} tarihinde stoklarda olacaktır.',
    subDescription: 'Ürün farklı bir fiyattan satışa sunulabilir. Eğer ürün daha yüksek bir fiyattan satışa sunulursa, ön sipariş veren müşteriden fiyat farkı alınmaz, ayrıca teslimatta öncelik sağlanır. Ürün daha düşük bir fiyattan satışa sunulursa, aradaki fark teslimatı takip eden 1 hafta içerisinde ön sipariş veren müşteriye ödenir.'
  },
  IS_NO_STOCK: {
    description: 'Bu ürün geçici olarak temin edilememektedir.',
    subDescription: 'Ürün temin edildiğinde tekrar satışa sunulacaktır.'
  },
  IS_CLOSED_PRODUCT: {
    description: 'Bu ürün şu an satılamamaktadır.'
  },
  IS_NO_COMBINATION: ' kombinasyonunda ürün bulunamamaktadır.'
}

const TAB_IDS = {
  DESCRIPTION: 'Description',
  REVIEWS: 'Reviews',
  QUESTION_ANSWER: 'QuestionAnswers',
  BNPL: 'BNPL',
  INSTALLMENTS: 'Installments',
  LOAN_TAB: 'LoanOffer',
  RETURN_POLICY: 'ReturnPolicy'
}

const TAB_CLICK_TYPES = {
  [TAB_IDS.DESCRIPTION]: DAVINCI_EVENT_NAMES.DESCRIPTION_TAB_CLICK,
  [TAB_IDS.REVIEWS]: DAVINCI_EVENT_NAMES.COMMENT_TAB_CLICK,
  [TAB_IDS.QUESTION_ANSWER]: DAVINCI_EVENT_NAMES.QUESTION_ANSWER_TAB_CLICK,
  [TAB_IDS.BNPL]: DAVINCI_EVENT_NAMES.BNPL_TAB_CLICK,
  [TAB_IDS.INSTALLMENTS]: DAVINCI_EVENT_NAMES.INSTALLMENT_TAB_CLICK,
  [TAB_IDS.LOAN_TAB]: DAVINCI_EVENT_NAMES.LOAN_TAB_CLICK,
  [TAB_IDS.RETURN_POLICY]: DAVINCI_EVENT_NAMES.REFUND_TAB_CLICK
}

const TAB_EVENT_LISTENER = 'TabChangeListener';

const VARIANT_TYPE_NAMES = {
  'Renk Tonu': 'color-tone'
};

export {
  INTERSECTION_ROOT_MARGIN,
  PRODUCT_DETAIL_VIEW_TYPES,
  PRODUCT_DETAIL_VIEW_TYPES_NAME,
  PRODUCT_DETAIL_CAROUSEL_IMAGE_SIZES,
  PRODUCT_DETAIL_EXTENDED_CAROUSEL_IMAGE_SIZES,
  PRODUCT_DETAIL_EXTENDED_CAROUSEL_THUMBNAIL_SIZES,
  PRODUCT_DETAIL_COMPONENTS,
  PRODUCT_DETAIL_CLASSNAME,
  EVENT_BUS_NAMES,
  ADD_TO_CART_BUTTON_TEXT,
  PRODUCT_INFORMATION_TEXT,
  TAB_CLICK_TYPES,
  TAB_IDS,
  TAB_EVENT_LISTENER,
  PDP_LAYOUT,
  VARIANT_TYPE_NAMES
}
